import React, { useContext, useState, useEffect, useRef } from 'react';
import { Listbox } from '@headlessui/react';
import { ThemeProvider } from 'emotion-theming';
import classNames from 'classnames';
import { genThemedStyled, Theme, ThemeContext } from '../../../utilities/theme';
import {
  ColorCodes,
  colorsForTheme,
  FUTURO as C,
} from '../../../foundations/color';
import S from '../../../foundations/size';
import { FUTURO_TYPEFACES as F } from '../../../foundations/font';
import Icon from '../../../foundations/Icons';

export type SelectOptions = {
  key: string;
  name: string;
  subtext?: string;
  tag?: string;
};

export type SelectSize = 'small' | 'medium' | 'large';

export type SelectProps = {
  /** The select input's options or selections */
  options: SelectOptions[];
  /** The select input's name */
  name: string;
  size?: SelectSize;
  /** The select input's value. by providing this prop, it will override the value state declared in this component. */
  selectedValue?: string;
  /** Set to true if the select has a blank option as the first option */
  noBlankOption?: boolean;
  /** select input is disabled (readonly) */
  disabled?: boolean;
  /** error state of the select input */
  hasError?: boolean;
  /** select input label */
  label?: string;
  /** hint text for select input */
  hint?: string;
  /** error label and text for select input */
  errorLabel?: string;
  /** event when the select value is changed. by providing this prop, it will override the onChange function declared in this component. */
  onChange: (name: string, value: string) => void;
  /** styles the overflow for the select option group */
  overflow?: boolean;
  overlayHeight?: string;
  noPadding?: boolean;
  /** the select input's placeholder */
  placeholder?: string;
  /** if the select box is inside a table, it will change the select css to be flexible inside a table */
  isInTable?: boolean;
  titleTooltip?: React.ReactNode;
};

const getTheme = (
  theme: Theme,
  { hasError, disabled }: SelectProps
): themeStyles => {
  const key = theme === 'futuro-dark' ? 'DARK' : 'LIGHT';
  const colors = colorsForTheme(theme);
  let borderColor;
  if (hasError) {
    borderColor = colors.ROSE;
  } else if (disabled) {
    borderColor = colors.STONE;
  } else {
    borderColor = colors.SHADOW;
  }

  let selectedBackgroundColor;
  let selectedTextColor;
  if (key === 'DARK') {
    selectedBackgroundColor = C.DARK.SMOG;
    selectedTextColor = C.DARK.SPACE;
  } else {
    selectedBackgroundColor = C.LIGHT.LIGHT_PEACH;
    selectedTextColor = C.LIGHT.SWEET_ORANGE;
  }

  let iconColor;
  if (disabled) {
    iconColor = colors.SHADOW;
  } else {
    iconColor = colors.SWEET_ORANGE;
  }

  const themeObj = {
    backgroundColor: colors.SWAN,
    borderColor,
    focusBorderColor: colors.SPACE,
    disabledBackgroundColor: colors.SMOKE,
    selectedBackgroundColor,
    selectedTextColor,
    labelColor: colors.SPACE,
    hintColor: colors.STEEL,
    errorLabelColor: colors.ROSE,
    disabledLabelColor: colors.STEEL,
    disabledHintColor: colors.SHADOW,
    textColor: colors.STORM,
    disabledTextColor: colors.STEEL,
    iconColor,
  };
  return themeObj;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  borderColor: ColorCodes;
  focusBorderColor: ColorCodes;
  disabledBackgroundColor: ColorCodes;
  selectedBackgroundColor: ColorCodes;
  selectedTextColor: ColorCodes;
  labelColor: ColorCodes;
  hintColor: ColorCodes;
  errorLabelColor: ColorCodes;
  disabledLabelColor: ColorCodes;
  disabledHintColor: ColorCodes;
  textColor: ColorCodes;
  disabledTextColor: ColorCodes;
  iconColor: ColorCodes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const SelectWrapper = ThemeStyled.div<{
  size?: SelectSize;
  noPadding?: boolean;
}>(({ size, noPadding }) => ({
  width: '100%',
  height: size === 'small' ? S.XXXL : '100%',
  position: 'relative',
  paddingTop: noPadding ? 'unset' : S.XXS,
}));

const SelectInput = ThemeStyled.button<{
  theme: themeStyles;
  size?: SelectSize;
  hasSelectedValue?: boolean;
  disabled?: boolean;
}>(
  ({ theme, hasSelectedValue, size, disabled }) => ({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    justifyContent: hasSelectedValue ? 'space-between' : 'flex-end',
    alignItems: 'center',
    padding: S.S,
    gap: S.M,
    width: 'inherit',
    height: 'inherit',
    background: theme.backgroundColor,
    border: `0.125rem solid ${theme.borderColor}`,
    color: theme.textColor,
    borderRadius: S.XXXS,
    maxHeight: size === 'small' ? S.XXXL : 'unset',
    cursor: disabled ? 'not-allowed' : 'unset',
    '&:focus': {
      border: `0.125rem solid ${theme.focusBorderColor}`,
      outline: 'none',
    },

    '&:disabled': {
      background: theme.disabledBackgroundColor,
      color: theme.disabledTextColor,
    },
  }),
  F.LABEL
);

const ButtonTextWrapper = ThemeStyled.div<{
  theme: themeStyles;
  disabled?: boolean;
}>(
  ({ theme, disabled }) => ({
    color: disabled ? theme.disabledTextColor : theme.textColor,
    textAlign: 'left',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  }),
  F.LABEL
);

const SelectOptionGroup = ThemeStyled.div<{
  overflow?: boolean;
  overlayHeight?: string;
}>(({ overflow, overlayHeight = '18.5rem' }) => ({
  boxShadow: `0 ${S.XS} ${S.XL} rgba(0, 0, 0, 0.08)`,
  borderRadius: `0 0 ${S.XXXS} ${S.XXXS}`,
  height: overflow ? 'unset' : 'auto',
  position: 'absolute',
  left: 0,
  right: 0,
  zIndex: 2,
  maxHeight: overflow ? overlayHeight : 'unset',
  overflow: 'auto',
  '&:focus-visible': {
    outline: 'none',
  },
}));

const SelectIconWrapper = ThemeStyled.div<{
  theme: themeStyles;
  open: boolean;
}>(({ theme, open }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  transition: '0.2s',
  transform: open ? 'rotateX(180deg)' : 'none',
  color: theme.iconColor,
}));

const HintWrapper = ThemeStyled.label<{
  disabled?: boolean;
  theme: themeStyles;
}>(
  ({ disabled, theme }) => ({
    color: disabled ? theme.disabledHintColor : theme.hintColor,
  }),
  F.LABEL_SMALL
);

const ErrorTextWrapper = ThemeStyled.div({
  paddingTop: S.XXXS,
  display: 'flex',
  flexDirection: 'column',
});

const ErrorLabelWrapper = ThemeStyled.label(
  p => ({
    color: p.theme.errorLabelColor,
  }),
  F.LABEL_SMALL_BOLD
);

/**
 * A futuro select input
 *
 * @argument options array - required prop. the select input's options or selections
 * @argument name string - optional prop. the select input's name
 * @argument value string - optional prop. the select input's value. by providing this prop, it will override the value state declared in this component.
 * @argument noBlankOption boolean - optional prop. set to true if the select has a blank option as the first option
 * @argument disabled boolean - optional prop. select input is disabled (readonly)
 * @argument hasError boolean - optional prop. error state of the select input
 * @argument label string - optional prop. select input label
 * @argument hint string - optional prop. hint text for select input
 * @argument errorLabel string - optional prop. error label and text for select input
 * @argument checked boolean - optional prop. checked state of the select input
 * @argument onChange - required prop. event when the select value is changed. by providing this prop, it will override the onChange function declared in this component.
 * @argument overflow - optional prop. styles the overflow for the select option group
 * @argument overlayHeight - optional prop. styles the max height of the select option group
 * @argument placeholder - optional prop. the select input's placeholder
 * @argument isInTable - optional prop. if the select box is inside a table, it will change the select css to be flexible inside a table
 * Based off the designs in the figma file:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=1161%3A4715
 *
 * @example
 *
 * const options= [
 *   { key: 'in-stock', name: 'In Stock' },
 *   { key: 'out-of-stock', name: 'Out of Stock' },
 * ]
 *
 * <Select
 *  name='select'
 *  options={options}
 *  label='Select Label'
 *  hint='Select a value from the list'
 * />
 */

export const Select = (props: SelectProps) => {
  const {
    options,
    name,
    size,
    selectedValue,
    noBlankOption = false,
    disabled = false,
    hasError = false,
    label,
    hint,
    errorLabel,
    onChange,
    isInTable = false,
    overflow = false,
    overlayHeight = '18.5rem',
    placeholder,
    noPadding = true,
    titleTooltip,
  } = props;
  const [selectValue, setSelectValue] = useState<any>([]);
  const selectValueRef = useRef(selectValue);
  selectValueRef.current = selectValue;

  const handleChange = event => {
    setSelectValue(event);
    onChange(name, event.key);
  };

  useEffect(() => {
    let initialValue: any = selectedValue
      ? options.filter(op => op.key === selectedValue)[0]
      : '';
    // handle the case where the selectedValue has not changed
    // because the parent component is not updating the selectedValue
    // when things change. See DeliveryDatePicker as an example
    const currentValue = selectValueRef.current;
    if (
      initialValue === '' &&
      !(currentValue instanceof Array && currentValue.length === 0)
    ) {
      initialValue = currentValue;
    }
    if (noBlankOption && initialValue === '') {
      // eslint-disable-next-line prefer-destructuring
      initialValue = options[0];
    }
    setSelectValue(initialValue);
  }, [selectedValue, options, noBlankOption]);

  return (
    <ThemeProvider theme={getTheme(useContext(ThemeContext), { ...props })}>
      {label && (
        <div className="flex flex-col gap-xxs py-xxxs">
          <div className="flex gap-xxs">
            <label
              className={classNames('font-body text-label-small text-strong', {
                'text-subtle': disabled,
              })}
              htmlFor={name}
            >
              {label}
            </label>
            {titleTooltip}
          </div>

          {hint && <HintWrapper disabled={disabled}>{hint}</HintWrapper>}
        </div>
      )}
      <Listbox
        as={SelectWrapper}
        className={isInTable ? 'absolute' : ''}
        name={name}
        value={selectValue}
        onChange={handleChange}
        disabled={disabled}
        size={size}
        role="listbox"
        aria-label="listbox-options"
        noPadding={noPadding}
        by="key"
      >
        {({ open }) => (
          <>
            <Listbox.Button
              className={classNames('!border-foreground-subtle', {
                relative: isInTable,
              })}
              data-testid="dropdown-element"
              as={SelectInput}
              hasSelectedValue={
                (selectValue && selectValue.name) || placeholder
              }
              size={size}
              // @ts-expect-error - something change in headlessui types
              disabled={disabled}
            >
              <ButtonTextWrapper disabled={disabled}>
                {selectValue ? selectValue.name : placeholder}
              </ButtonTextWrapper>
              <SelectIconWrapper open={open}>
                <Icon name="sChevronDown" inheritFill />
              </SelectIconWrapper>
            </Listbox.Button>
            <Listbox.Options
              data-testid="dropdown-options"
              as={SelectOptionGroup}
              overflow={overflow}
              overlayHeight={overlayHeight}
            >
              {options &&
                options.map(option => (
                  <Listbox.Option
                    key={option.key}
                    value={option}
                    className="group box-border flex cursor-pointer list-none flex-col p-s bg-regular hover:bg-special aria-selected:bg-special"
                  >
                    <p className="text-body text-regular group-hover:text-primary-brand group-aria-selected:text-primary-brand">
                      {option.name}
                    </p>
                    {option.subtext && (
                      <p className="text-body-small text-regular">
                        {option.subtext}
                      </p>
                    )}
                  </Listbox.Option>
                ))}
            </Listbox.Options>
          </>
        )}
      </Listbox>
      {hasError && (
        <ErrorTextWrapper>
          <ErrorLabelWrapper>{errorLabel}</ErrorLabelWrapper>
        </ErrorTextWrapper>
      )}
    </ThemeProvider>
  );
};

export default Select;
